import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faMicrophone, faMicrophoneSlash, faPhoneSlash, faWindowMaximize, faVideo, faComment, faXmark
} from '@fortawesome/free-solid-svg-icons';
import useMeetingRoom from '../hooks/useMeetingRoom';
import useMeetingSession from '../hooks/useMeetingSession';
import { useCall } from './CallContext';
import { MeetingContext, useMeeting } from './meetingContextCore';
import { employeeMeetingApi } from '../utils/meetingApi';
import MeetingWindow from '../components/meeting/MeetingWindow';
import MeetingRoomView from '../components/meeting/MeetingRoomView';
import { shareLinkOf } from '../utils/copyLink';
import '../styles/meetings.css';

/**
 * The meeting a person is in, kept ABOVE the page routes.
 *
 * The room used to belong to the meeting page, so going to another page - or
 * anything else that unmounted it - dropped the person out of the meeting.
 * Here it belongs to the signed-in shell (App.js), exactly as a chat call does
 * (context/CallContext.js): moving between pages leaves it untouched, and it
 * is a window that can be moved, resized, minimised to a pill, or maximised.
 *
 * Employees only. An outside guest has no pages to move between, so their page
 * (pages/GuestMeeting.js) shows the room directly.
 *
 * Kept apart from the chat call: its own connection, its own devices. The one
 * place they meet is that starting or answering a call takes the microphone,
 * so a meeting is left when a call begins.
 */

export { useMeeting };

/** Minimised: who you are with, and the three things you need - mute, open, leave. */
const MeetingPill = ({ title, room, unread, notice, onExpand, onLeave }) => (
    <div className={`mt-pill ${notice ? 'is-alert' : ''}`} role="status">
        <button type="button" className="mt-pill-open" onClick={onExpand} title="Open the meeting">
            <FontAwesomeIcon icon={faVideo} />
            <span className="mt-pill-title">{room.status === 'reconnecting' ? 'Reconnecting…' : title}</span>
            {unread > 0 && <span className="mt-pill-badge"><FontAwesomeIcon icon={faComment} /> {unread > 9 ? '9+' : unread}</span>}
            {notice && <span className="mt-pill-badge alert">Action needed</span>}
        </button>
        <button type="button" className={`mt-pill-btn ${room.micOn ? '' : 'off'}`} onClick={room.toggleMic} title={room.micOn ? 'Mute' : 'Unmute'} aria-label={room.micOn ? 'Mute' : 'Unmute'}>
            <FontAwesomeIcon icon={room.micOn ? faMicrophone : faMicrophoneSlash} />
        </button>
        <button type="button" className="mt-pill-btn" onClick={onExpand} title="Open" aria-label="Open the meeting">
            <FontAwesomeIcon icon={faWindowMaximize} />
        </button>
        <button type="button" className="mt-pill-btn leave" onClick={onLeave} title="Leave" aria-label="Leave the meeting">
            <FontAwesomeIcon icon={faPhoneSlash} />
        </button>
    </div>
);

/** What shows once the meeting is over for you, however it ended. */
const EndedCard = ({ message, onClose }) => (
    <div className="mt-pill mt-ended" role="status">
        <span>{message || 'You left the meeting.'}</span>
        <button type="button" className="mt-pill-btn" onClick={onClose} aria-label="Close"><FontAwesomeIcon icon={faXmark} /></button>
    </div>
);

/**
 * The meeting window or its pill. A separate component, keyed by the meeting, so
 * that the chat and the lock state start fresh for each meeting and are kept
 * while this one lasts - including while it is minimised.
 */
const END_WARNING_MS = 5 * 60 * 1000;

const ActiveMeeting = ({ active, room, view, setView, onLeave, onClose }) => {
    const session = useMeetingSession({ client: active.client, initialLocked: active.initialLocked });

    // Five minutes before the end, a host is asked whether to carry on. Nothing ends by itself:
    // the meeting runs until somebody ends it, so this is only ever a question, never a cut-off.
    const [endsAt, setEndsAt] = useState(active.endsAt);
    const [dismissedFor, setDismissedFor] = useState(null);
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        if (!active.canHost) return undefined;
        const timer = setInterval(() => setNow(Date.now()), 15000);
        return () => clearInterval(timer);
    }, [active.canHost]);
    const msLeft = endsAt ? new Date(endsAt).getTime() - now : Infinity;
    const warn = Boolean(active.canHost) && room.status === 'connected' && msLeft <= END_WARNING_MS && dismissedFor !== String(endsAt);
    // It must be seen, even if the meeting is shrunk to its pill.
    useEffect(() => { if (warn) setView('window'); }, [warn, setView]);
    const endWarning = warn ? {
        minutesLeft: Math.max(0, Math.ceil(msLeft / 60000)),
        onExtend: async (minutes) => { const res = await active.client.extend(minutes); setEndsAt(res.endsAt); },
        onDismiss: () => setDismissedFor(String(endsAt))
    } : null;

    // The person asked to stop sharing might be looking at something else:
    // bring the meeting back in front so they see it.
    useEffect(() => { if (session.notice) setView('window'); }, [session.notice, setView]);

    if (room.status === 'ended') return <EndedCard message={room.message} onClose={onClose} />;

    if (view === 'pill') {
        return (
            <MeetingPill
                title={active.title}
                room={room}
                unread={session.chat.unread}
                notice={session.notice}
                onExpand={() => setView('window')}
                onLeave={onLeave}
            />
        );
    }

    return (
        <MeetingWindow title={active.title} status={room.status} onMinimize={() => setView('pill')}>
            <MeetingRoomView
                title={active.title}
                room={room}
                session={session}
                canHost={active.canHost}
                onLeave={onLeave}
                shareLink={active.joinLink}
                inviteMeetingId={active.id}
                endWarning={endWarning}
                embedded
            />
        </MeetingWindow>
    );
};

export const MeetingProvider = ({ children }) => {
    const room = useMeetingRoom();
    const callContext = useCall();
    const [active, setActive] = useState(null); // { id, title, canHost, client, initialLocked }
    const [view, setView] = useState('window'); // 'window' | 'pill'

    const { status: roomStatus, disconnect, connect } = room;
    const inMeeting = roomStatus === 'connecting' || roomStatus === 'connected' || roomStatus === 'reconnecting';

    const chatCall = callContext?.call?.status;
    const onChatCall = Boolean(chatCall) && chatCall !== 'idle';

    // A chat call and a meeting both want the microphone: the call wins.
    useEffect(() => {
        if (onChatCall && (roomStatus === 'connected' || roomStatus === 'reconnecting')) {
            disconnect('You joined a call, so you left the meeting.');
        }
    }, [onChatCall, roomStatus, disconnect]);

    // Closing the tab or reloading the page ends the meeting for you: say so first.
    useEffect(() => {
        if (!inMeeting) return undefined;
        const warn = (event) => { event.preventDefault(); event.returnValue = ''; };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [inMeeting]);

    /** Join with the answer from POST /meetings/:id/join. */
    const enter = useCallback(async ({ meeting, join, choices }) => {
        setActive({
            id: String(meeting._id),
            title: meeting.title,
            canHost: Boolean(join.meeting?.canHost),
            initialLocked: Boolean(meeting.locked),
            joinLink: join.meeting?.joinLink || shareLinkOf(meeting),
            endsAt: join.meeting?.endsAt || meeting.endsAt,
            client: employeeMeetingApi(meeting._id)
        });
        setView('window');
        await connect({ ...join, ...choices });
    }, [connect]);

    const leave = useCallback(async () => {
        await disconnect('');
        setActive(null);
    }, [disconnect]);

    const value = useMemo(() => ({
        activeId: active ? active.id : null,
        inMeeting,
        enter,
        leave,
        expand: () => setView('window'),
        minimize: () => setView('pill')
    }), [active, inMeeting, enter, leave]);

    return (
        <MeetingContext.Provider value={value}>
            {children}
            {active && (
                <ActiveMeeting
                    key={active.id}
                    active={active}
                    room={room}
                    view={view}
                    setView={setView}
                    onLeave={leave}
                    onClose={() => setActive(null)}
                />
            )}
        </MeetingContext.Provider>
    );
};

export default MeetingContext;
